import { Grid, Typography } from '@mui/material';
import { KnownUploadImageDtoCategory } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Control } from 'react-hook-form';
import { ImageUpload } from '../../../../ui/components/higher-order/image-upload/image-upload.component';
import { FormControl } from '../../../../ui/components/pure/form-control/form-control.component';
import { TextField } from '../../../../ui/components/pure/text-field/text-field.component';
import { validateSortOrder } from '../../logic/carrier-sort-order';
import { fullWidthStyle, rowGroupStyle } from './carrier-form-modal.component';

export type CarrierFormState = {
  name: string;
  shortName: string;
  image: string;
  /** Edit only, for a network pool's carriers. Kept as typed so validation sees exactly what was entered. */
  searchPriority?: string;
};

/** Name, short name and logo, shared by adding and editing a carrier, plus the sort order when `sortOrder` is given. */
export function CarrierFormFields({
  control,
  image,
  onImageUploaded,
  sortOrder
}: {
  control: Control<CarrierFormState>;
  image?: string;
  onImageUploaded(url: string): void;
  /** `isSet`: the carrier already has a sort order, which the API cannot clear. */
  sortOrder?: { isSet: boolean };
}) {
  return (
    <Grid container spacing={2} style={rowGroupStyle}>
      <Grid size={12}>
        <FormControl
          control={control}
          name="name"
          required
          rules={{ validate: (value: string) => (value?.trim() ? true : 'Name is required') }}
          ComponentProps={{
            textFieldVariant: 'floating' as const,
            placeholder: 'Name',
            fullWidth: true,
            style: fullWidthStyle
          }}
          Component={TextField}
        />
      </Grid>
      <Grid size={12}>
        {/* What the kiosk looks a carrier up by when identifying a courier. */}
        <FormControl
          control={control}
          name="shortName"
          ComponentProps={{
            textFieldVariant: 'floating' as const,
            placeholder: 'Short name (optional)',
            fullWidth: true,
            style: fullWidthStyle
          }}
          Component={TextField}
        />
      </Grid>
      {sortOrder && (
        <Grid size={12}>
          {/* Marked required once set, as blank would otherwise read as clearing it. */}
          <FormControl
            control={control}
            name="searchPriority"
            required={sortOrder.isSet || undefined}
            rules={{ validate: validateSortOrder }}
            ComponentProps={{
              textFieldVariant: 'floating' as const,
              placeholder: sortOrder.isSet ? 'Sort order' : 'Sort order (optional)',
              slotProps: { htmlInput: { inputMode: 'numeric' as const } },
              fullWidth: true,
              style: fullWidthStyle
            }}
            Component={TextField}
          />
        </Grid>
      )}
      <Grid
        container
        spacing={2}
        wrap="nowrap"
        size={12}
        sx={{
          alignItems: 'center'
        }}
      >
        <Grid>
          {/* Public, so the kiosk and the mobile app can read it without a token. */}
          <ImageUpload
            id="carrier-logo-upload"
            isPublic
            category={KnownUploadImageDtoCategory.Group}
            onUploaded={onImageUploaded}
          >
            {image ? 'Replace logo' : 'Upload logo'}
          </ImageUpload>
        </Grid>
        {image ? (
          <Grid>
            <img src={image} alt="Carrier logo" style={{ maxHeight: '40px', maxWidth: '120px' }} />
          </Grid>
        ) : (
          <Grid>
            <Typography variant="body2" style={{ color: '#666' }}>
              No logo
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
